<template>
  <div class="panorama-page p-2 p-md-3" :class="{ 'dark-mode': isDarkMode }">
    <div class="d-flex align-items-center justify-content-between mb-2 flex-wrap">
      <div class="d-flex align-items-center flex-wrap mb-2 mb-md-0 panorama-toolbar">
        <h3 class="text-title mb-0 font-weight-bold tracking-wide mr-3">
          <b-icon icon="display" class="mr-2 text-warning"></b-icon>策略全景解析
        </h3>
        <div class="d-flex align-items-center panorama-pill px-2 py-1 rounded-pill border">
          <b-icon icon="calendar3" class="text-warning mx-2" font-scale="0.9"></b-icon>
          <b-form-input 
            type="date" 
            v-model="selectedDate" 
            size="sm" 
            class="custom-datepicker border-0 bg-transparent shadow-none px-1" 
            style="width: 135px; font-size: 0.875rem;"
            @change="initializePage"
          ></b-form-input>
          <b-button 
            variant="link" 
            size="sm" 
            class="text-warning p-0 mx-2 text-decoration-none xx-small" 
            @click="resetToToday"
            v-if="selectedDate !== todayStr"
          >
            今天
          </b-button>
        </div>
        <div class="d-flex align-items-center panorama-pill px-3 py-1 rounded-pill border ml-2">
          <b-form-checkbox
            v-model="supplementH67"
            @change="updateMonitorConfig()"
            switch
            class="panorama-switch xx-small"
          >
            <span style="font-size: 0.825rem; font-weight: 500; cursor: pointer;">补充H6/7</span>
          </b-form-checkbox>
          <b-form-checkbox
            v-model="filterTopicNet5"
            @change="updateMonitorConfig()"
            switch
            class="panorama-switch xx-small ml-3"
          >
            <span style="font-size: 0.825rem; font-weight: 500; cursor: pointer;">NET5≥0</span>
          </b-form-checkbox>
        </div>
        <b-button
          variant="outline-info"
          size="sm"
          class="rounded-pill px-3 ml-2"
          :disabled="refreshing"
          @click="refreshPage"
        >
          <b-spinner v-if="refreshing" small class="mr-1"></b-spinner>
          <b-icon v-else icon="arrow-clockwise" class="mr-1"></b-icon>刷新
        </b-button>

        <!-- 实盘自动同步开关（仅当日有效） -->
        <div
          class="d-flex align-items-center panorama-pill px-2 py-1 rounded-pill border ml-2 transition-all"
          :class="autoSync && isCurrentDateToday ? 'border-success-50' : ''"
          :title="!isCurrentDateToday ? '仅当日支持实盘自动同步' : (autoSync ? '已开启实盘自动同步（30秒/1分钟收到广播静默刷新）' : '已关闭自动同步，点击可开启')"
        >
          <b-form-checkbox
            v-model="autoSync"
            switch
            class="panorama-switch xx-small"
            :disabled="!isCurrentDateToday"
            @change="toggleAutoSync"
          >
            <span
              :class="autoSync && isCurrentDateToday ? 'text-success font-weight-bold' : 'text-muted'"
              style="font-size: 0.8rem; cursor: pointer; user-select: none;"
            >
              <span v-if="autoSync && isCurrentDateToday" class="pulse-dot mr-1">●</span>
              {{ autoSync && isCurrentDateToday ? '自动同步' : '手动模式' }}
            </span>
          </b-form-checkbox>
          <span
            v-if="lastSyncTime && autoSync && isCurrentDateToday"
            class="text-muted ml-1 font-monospace"
            style="font-size: 0.72rem; opacity: 0.85;"
            :title="`最后静默同步时间: ${lastSyncTime}`"
          >
            {{ lastSyncTime }}
          </span>
        </div>
      </div>
      <b-button variant="outline-secondary" class="rounded-pill px-4 shadow-sm" @click="$router.back()">
        <b-icon icon="arrow-left" class="mr-1"></b-icon> 返回
      </b-button>
    </div>

    <div class="row mb-2 compact-control-panel">
      <div class="col-12 col-xl-7 mb-2 mb-xl-0">
        <b-card class="panorama-card shadow-sm rounded-lg" body-class="p-2">
          <div class="d-flex justify-content-between align-items-center flex-wrap mb-1">
            <div>
              <span class="font-weight-bold text-title">仓位配置</span>
              <span class="text-muted xx-small ml-2">模拟盘与实盘独立控制（配置0%表示不开仓），保存后下一周期生效</span>
            </div>
            <b-button size="sm" variant="warning" class="rounded-pill px-3" :disabled="savingConfig" @click="savePositionConfig">
              <b-spinner v-if="savingConfig" small class="mr-1"></b-spinner>保存仓位
            </b-button>
          </div>

          <!-- V2 模拟盘 -->
          <div class="position-config-row mb-1">
            <div class="strategy-label text-warning">V2模拟</div>
            <div v-for="(value, index) in v2PositionPcts" :key="`v2-sim-${index}`" class="position-input-wrap">
              <label>第{{ index + 1 }}笔<span v-if="Number(v2PositionPcts[index]) === 0" class="text-danger ml-1">不开仓</span></label>
              <b-input-group size="sm" append="%">
                <b-form-input v-model.number="v2PositionPcts[index]" type="number" min="0" max="100" step="0.5" placeholder="0"></b-form-input>
              </b-input-group>
            </div>
            <div class="position-total">
              模拟总仓位 <strong>{{ positionTotal(v2PositionPcts) }}%</strong>
              <span class="today-buy-targets ml-2">
                ｜ 当日买入：
                <template v-if="todayBuys.v2 && todayBuys.v2.length">
                  <span v-for="(item, idx) in todayBuys.v2" :key="`v2-target-${item.code || idx}`" class="today-buy-target-item">
                    <strong class="target-name">{{ item.name }}</strong>
                    <b-button
                      variant="link"
                      size="sm"
                      class="fire-buy-btn p-0"
                      title="点击点火触发实盘买入"
                      @click.stop="openRealBuyModal(item, 'v2', idx)"
                    >
                      🔥
                    </b-button>
                    <span v-if="idx < todayBuys.v2.length - 1" class="text-secondary mr-1">、</span>
                  </span>
                </template>
                <strong v-else class="text-muted">暂无</strong>
              </span>
            </div>
          </div>

          <!-- V2 实盘 -->
          <div class="position-config-row mb-2 border-bottom panorama-divider pb-1">
            <div class="strategy-label text-danger">V2实盘</div>
            <div v-for="(value, index) in v2RealPositionPcts" :key="`v2-real-${index}`" class="position-input-wrap">
              <label>第{{ index + 1 }}笔<span v-if="Number(v2RealPositionPcts[index]) === 0" class="text-danger ml-1">不开仓</span></label>
              <b-input-group size="sm" append="%">
                <b-form-input v-model.number="v2RealPositionPcts[index]" type="number" min="0" max="100" step="0.5" placeholder="0"></b-form-input>
              </b-input-group>
            </div>
            <div class="position-total">
              实盘总仓位 <strong>{{ positionTotal(v2RealPositionPcts) }}%</strong>
            </div>
          </div>

          <!-- V3 模拟盘 -->
          <div class="position-config-row mb-1">
            <div class="strategy-label text-info">V3模拟</div>
            <div v-for="(value, index) in v3PositionPcts" :key="`v3-sim-${index}`" class="position-input-wrap">
              <label>第{{ index + 1 }}笔<span v-if="Number(v3PositionPcts[index]) === 0" class="text-danger ml-1">不开仓</span></label>
              <b-input-group size="sm" append="%">
                <b-form-input v-model.number="v3PositionPcts[index]" type="number" min="0" max="100" step="0.5" placeholder="0"></b-form-input>
              </b-input-group>
            </div>
            <div class="position-total">
              模拟总仓位 <strong>{{ positionTotal(v3PositionPcts) }}%</strong>
              <span class="today-buy-targets ml-2">
                ｜ 当日买入：
                <template v-if="todayBuys.v3 && todayBuys.v3.length">
                  <span v-for="(item, idx) in todayBuys.v3" :key="`v3-target-${item.code || idx}`" class="today-buy-target-item">
                    <strong class="target-name">{{ item.name }}</strong>
                    <b-button
                      variant="link"
                      size="sm"
                      class="fire-buy-btn p-0"
                      title="点击点火触发实盘买入"
                      @click.stop="openRealBuyModal(item, 'v3', idx)"
                    >
                      🔥
                    </b-button>
                    <span v-if="idx < todayBuys.v3.length - 1" class="text-secondary mr-1">、</span>
                  </span>
                </template>
                <strong v-else class="text-muted">暂无</strong>
              </span>
            </div>
          </div>

          <!-- V3 实盘 -->
          <div class="position-config-row">
            <div class="strategy-label text-success">V3实盘</div>
            <div v-for="(value, index) in v3RealPositionPcts" :key="`v3-real-${index}`" class="position-input-wrap">
              <label>第{{ index + 1 }}笔<span v-if="Number(v3RealPositionPcts[index]) === 0" class="text-danger ml-1">不开仓</span></label>
              <b-input-group size="sm" append="%">
                <b-form-input v-model.number="v3RealPositionPcts[index]" type="number" min="0" max="100" step="0.5" placeholder="0"></b-form-input>
              </b-input-group>
            </div>
            <div class="position-total">
              实盘总仓位 <strong>{{ positionTotal(v3RealPositionPcts) }}%</strong>
            </div>
          </div>
        </b-card>
      </div>
      <div class="col-12 col-xl-5">
        <b-card class="crowding-card panorama-card shadow-sm rounded-lg h-100" :class="`crowding-${crowding.level || 'unknown'}`" body-class="p-2">
          <div class="d-flex justify-content-between align-items-start">
            <div><div class="font-weight-bold text-title">题材拥挤度</div><div class="xx-small text-muted mt-1" v-if="crowding.date">指标日期 {{ crowding.date }}<span v-if="crowding.is_stale" class="text-warning ml-1">（非当日数据）</span></div></div>
            <b-badge :variant="crowdingBadgeVariant" class="px-2 py-1">{{ crowdingLevelText }}</b-badge>
          </div>
          <div v-if="crowding.available" class="mt-1 d-flex align-items-center crowding-content">
            <div class="crowding-metric mr-3">
              <div class="d-flex align-items-center flex-wrap">
                <div class="crowding-ratio mr-2">{{ (crowding.positive_ratio * 100).toFixed(1) }}%</div>
                <div class="crowding-threshold-note">&lt;78% 正常 ｜ 78%–&lt;82% 半仓 ｜ ≥82% 禁止新开仓</div>
              </div>
              <div class="text-muted xx-small text-nowrap">
                正预期 {{ crowding.positive_count }}/{{ crowding.valid_count }}，覆盖率 {{ (crowding.coverage_ratio * 100).toFixed(1) }}%（{{ crowding.valid_count }}/{{ crowding.topic_count }}）
                <b-icon icon="question-circle" class="ml-1" title="覆盖率 = 具备有效历史统计的题材数 ÷ 当日题材总数；至少80%才启用拥挤判断"></b-icon>
              </div>
            </div>
            <div class="crowding-result">
              <div class="crowding-advice">{{ crowding.message }}</div>
              <div class="xx-small mt-1 text-muted" v-if="crowding.recommendations">V2 {{ recommendedPositionText('v2') }}；V3 {{ recommendedPositionText('v3') }}</div>
            </div>
          </div>
          <div v-else class="text-muted mt-3">{{ crowding.message || '拥挤度数据不可用' }}</div>
        </b-card>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="warning" label="Loading..."></b-spinner>
      <div class="text-muted-gold mt-3">正在获取 {{ selectedDate }} 监控数据...</div>
    </div>

    <div v-else-if="topics.length === 0" class="text-center py-5">
      <b-icon icon="inbox" font-scale="3" class="text-muted mb-3"></b-icon>
      <div class="text-muted">{{ selectedDate }} 无监控题材或暂未获取到数据</div>
    </div>

    <div v-else class="row no-gutters custom-row-5">
      <div v-for="(topic, index) in topics" :key="index" class="col-12 col-md-4 col-xl-20 mb-2 px-1">
        <b-card class="panorama-card shadow-sm h-100 rounded-lg overflow-hidden" body-class="p-1 p-md-2">
          <!-- 头部 -->
          <div class="d-flex justify-content-between align-items-start mb-2 pb-2 border-bottom panorama-divider">
            <div>
              <h6 class="mb-1 topic-name-title font-weight-bold cursor-pointer hover-primary d-flex align-items-center flex-wrap" :title="topic.topic_name" @click="showTopicKline(topic)">
                <span class="topic-sequence mr-1">{{ index + 1 }}.</span><span>{{ topic.topic_name }}</span>
                <span v-if="topic.h_marker || topic.d20_marker" class="text-muted-gold font-weight-normal xx-small ml-1" style="font-size: 0.75rem;">
                  [{{ [topic.h_marker, topic.d20_marker].filter(Boolean).join(',') }}]
                </span>
              </h6>
              <div class="d-flex align-items-center flex-wrap mb-1">
                <b-badge variant="soft-warning" class="xx-small mr-1 d-inline-block">共 {{ topic.total_candidates }} 备选</b-badge>
                <b-badge 
                  v-if="formatTopicNet5(topic)" 
                  :class="getNet5BadgeClass(topic)" 
                  class="xx-small font-weight-bold d-inline-block"
                  :title="`近5日净涨停: ${formatTopicNet5(topic)}`"
                >
                  NET5 {{ formatTopicNet5(topic) }}
                </b-badge>
                <b-badge 
                  v-if="topic.is_height_collapse" 
                  class="xx-small font-weight-bold d-inline-block ml-1 badge-collapse"
                  title="高度崩塌5→2：近5日最高连板>=5且前1日最高<=2，实盘V2风控排除该题材"
                >
                  崩盘5→2
                </b-badge>
              </div>
              <div class="text-muted xx-small" v-if="topic.stats">
                涨跌: <span class="text-danger">{{ topic.stats.up_count }}</span>/<span class="text-success">{{ topic.stats.down_count }}</span>
                <span class="mx-1">|</span>
                均: <span :class="getPnlClass(topic.stats.avg_pct)">{{ topic.stats.avg_pct.toFixed(2) }}%</span>
              </div>
              <div class="text-muted xx-small mt-1" style="max-height: 2.8em; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; line-height: 1.4;" v-if="topic.stats" :title="topic.stats.main_leader + ' (主) / ' + topic.stats.gem_leader + ' (创)'">
                领涨: <span class="leader-names">{{ topic.stats.main_leader }} / {{ topic.stats.gem_leader }}</span>
              </div>
              <div class="text-muted xx-small mt-1" style="max-height: 2.8em; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; line-height: 1.4;" :title="topic.stats.limit_ups" v-if="topic.stats">
                涨停: <span class="text-danger">{{ topic.stats.limit_ups }}</span>
              </div>
            </div>
            <div class="text-right">
              <div class="text-muted xx-small">触发/限额</div>
              <div class="counts-text xx-small mt-1">
                <span class="text-danger font-weight-bold">{{ topic.alerts.length }}</span>次
                <span class="mx-1">|</span>
                主<span :class="{'text-warning': topic.counts['主板'] >= 2}">{{ topic.counts['主板'] || 0 }}/2</span>
                <span class="mx-1">|</span>
                创<span :class="{'text-warning': topic.counts['双创'] >= 1}">{{ topic.counts['双创'] || 0 }}/1</span>
              </div>
            </div>
          </div>
          
          <!-- 备选股列表 -->
          <div class="table-responsive">
            <table class="table panorama-table table-hover table-borderless table-sm mb-0 compact-table xx-small">
              <thead>
                <tr class="text-muted border-bottom panorama-divider">
                  <th style="width: 25%">股票</th>
                  <th class="text-right" style="width: 13%">现价</th>
                  <th class="text-right text-nowrap" style="width: 16%">额/值</th>
                  <th class="text-right text-nowrap" style="width: 15%">涨幅</th>
                  <th class="text-center text-nowrap" style="width: 31%">状态</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="stock in getDisplayCandidates(topic.candidates, topic.topic_name)" :key="stock.code6">
                  <td class="align-middle text-nowrap">
                    <a href="javascript:void(0)" @click="showStockInfo(stock.code6, stock.name)" class="stock-name-link text-decoration-none hover-primary font-weight-bold text-truncate d-inline-block align-middle" style="max-width: 58px;" :title="stock.name + ' (' + stock.code6 + ')'">
                      {{ stock.name }}
                    </a>
                    <span
                      v-if="stock.score !== undefined && stock.score !== null"
                      class="score-pill ml-1 align-middle"
                      :class="getScoreClass(stock.score)"
                      :title="getScoreTooltip(stock)"
                    >
                      {{ stock.score > 0 ? '+' : '' }}{{ stock.score }}
                    </span>
                    <span class="text-muted ml-1 align-middle font-monospace" style="font-size: 0.6rem; letter-spacing: -0.2px;">{{ stock.code6 }}</span>
                  </td>
                  <td class="text-right align-middle font-weight-bold font-monospace" :class="getPnlClass(stock.pct_change)">
                    {{ stock.price.toFixed(2) }}
                  </td>
                  <td class="text-right align-middle text-muted text-nowrap font-monospace" style="font-size: 0.62rem;">
                    {{ formatAmountMkv(stock.amount, stock.mkv) }}
                  </td>
                  <td class="text-right align-middle font-weight-bold text-nowrap font-monospace" :class="getPnlClass(stock.pct_change)">
                    {{ stock.pct_change > 0 ? '+' : '' }}{{ stock.pct_change.toFixed(2) }}%
                  </td>
                  <td class="text-center align-middle py-0 px-1">
                    <div class="d-flex align-items-center justify-content-center text-nowrap" :title="getStatusTooltip(stock)">
                      <!-- 1. 智能合并单标签 (当 V2 与 V3 状态一致时，只展示一个精致单标签，消除80%冗余噪音) -->
                      <template v-if="isStatusMerged(stock)">
                        <b-badge :class="getMergedBadgeClass(stock)" class="status-badge-compact text-nowrap">
                          {{ getMergedBadgeText(stock) }}
                        </b-badge>
                      </template>

                      <!-- 2. 状态分化展示 (单行紧凑双胶囊，左右并列) -->
                      <template v-else>
                        <span class="micro-badge" :class="getV2BadgeClass(stock)">
                          <span class="micro-prefix v2-p">2:</span>{{ getV2BadgeText(stock) }}
                        </span>
                        <span class="micro-badge ml-1" :class="getV3BadgeClass(stock)">
                          <span class="micro-prefix v3-p">3:</span>{{ getV3BadgeText(stock) }}
                        </span>
                      </template>

                      <!-- 手动点火微型按钮 (仅今天且V2未买入/未触发时显示) -->
                      <b-button 
                        v-if="selectedDate === todayStr && isCanManualTrigger(stock)"
                        variant="link" 
                        size="sm" 
                        class="p-0 ml-1 fire-btn" 
                        title="手动点火买入"
                        @click.stop="confirmManualTrigger(stock.code6, stock.name)"
                      >
                        🔥
                      </b-button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="topic.candidates.length > 10" class="text-center mt-1">
              <b-button variant="link" size="sm" class="text-muted xx-small p-0" @click="toggleExpand(topic.topic_name)">
                {{ expandedTopics[topic.topic_name] ? '收起' : `查看更多 (${topic.candidates.length - 10})` }}
                <b-icon :icon="expandedTopics[topic.topic_name] ? 'chevron-up' : 'chevron-down'"></b-icon>
              </b-button>
            </div>
          </div>
        </b-card>
      </div>
    </div>
    
    <!-- 引入个股侧边栏 -->
    <StockInfoSidebar 
      :symbol_code="sidebarSymbolCode" 
      :symbol_name="sidebarSymbolName" 
    />

    <!-- 题材K线图模态框 -->
    <TopicKlineModal
      :visible="showTopicKlineModal"
      :uniqueKey="selectedTopicUniqueKey"
      :topicName="selectedTopicName"
      @close="showTopicKlineModal = false"
    />

    <!-- 实盘买入点火模态弹窗 -->
    <b-modal
      id="real-buy-modal"
      v-model="showRealBuyModal"
      title="🔥 实盘买入点火确认"
      :header-bg-variant="isDarkMode ? 'dark' : 'light'"
      :header-text-variant="isDarkMode ? 'light' : 'dark'"
      :body-bg-variant="isDarkMode ? 'dark' : 'light'"
      :body-text-variant="isDarkMode ? 'light' : 'dark'"
      :footer-bg-variant="isDarkMode ? 'dark' : 'light'"
      :footer-text-variant="isDarkMode ? 'light' : 'dark'"
      centered
      no-close-on-backdrop
    >
      <div class="real-buy-modal-content">
        <!-- 标的概要 -->
        <div class="target-summary-card p-3 rounded mb-3">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <div class="h5 mb-0 text-title font-weight-bold">
                {{ realBuyForm.name }}
                <span class="text-muted-gold ml-2 font-monospace" style="font-size: 0.95rem;">{{ realBuyForm.code }}</span>
              </div>
              <div class="xx-small text-muted mt-1">模拟买入触发标的 ｜ 准备向券商实盘通道提交买入委托</div>
            </div>
            <b-badge variant="danger" class="px-2 py-1">实盘点火</b-badge>
          </div>
        </div>

        <!-- 选择实盘策略 -->
        <div class="form-group mb-3">
          <label class="font-weight-bold text-title d-flex justify-content-between align-items-center">
            <span>选择实盘策略 <span class="text-danger">*</span></span>
            <span class="xx-small text-muted font-weight-normal">默认匹配模拟触发策略</span>
          </label>
          <div class="d-flex strategy-select-group">
            <b-button
              class="flex-fill mr-2 py-2 text-center"
              :variant="realBuyForm.strategy === 'v2' ? 'warning' : 'outline-secondary'"
              @click="onRealBuyStrategyChange('v2')"
            >
              <div class="font-weight-bold">实盘 V2</div>
              <div class="xx-small mt-1 opacity-80">当前配置总仓位 {{ positionTotal(v2RealPositionPcts) }}%</div>
            </b-button>
            <b-button
              class="flex-fill py-2 text-center"
              :variant="realBuyForm.strategy === 'v3' ? 'info' : 'outline-secondary'"
              @click="onRealBuyStrategyChange('v3')"
            >
              <div class="font-weight-bold">实盘 V3</div>
              <div class="xx-small mt-1 opacity-80">当前配置总仓位 {{ positionTotal(v3RealPositionPcts) }}%</div>
            </b-button>
          </div>
        </div>

        <!-- 调整买入仓位 -->
        <div class="form-group mb-3">
          <label class="font-weight-bold text-title d-flex justify-content-between align-items-center">
            <span>买入仓位比例 <span class="text-danger">*</span></span>
            <span class="xx-small text-muted font-weight-normal">
              默认配置: {{ getDefaultRealPositionPct(realBuyForm.strategy, realBuyForm.targetIdx) }}%
            </span>
          </label>
          <b-input-group append="%">
            <b-form-input
              v-model.number="realBuyForm.positionPct"
              type="number"
              min="0.1"
              max="100"
              step="0.5"
              placeholder="请输入买入仓位比例"
            ></b-form-input>
          </b-input-group>

          <!-- 常用快捷仓位选择 -->
          <div class="d-flex align-items-center flex-wrap mt-2">
            <span class="xx-small text-muted mr-2">快捷:</span>
            <b-button
              v-for="p in [5, 10, 15, 20]"
              :key="p"
              size="sm"
              variant="outline-secondary"
              class="px-2 py-0 mr-1 mb-1 xx-small"
              :class="{ 'btn-info text-white': Number(realBuyForm.positionPct) === p }"
              @click="realBuyForm.positionPct = p"
            >
              {{ p }}%
            </b-button>
            <b-button
              size="sm"
              variant="outline-warning"
              class="px-2 py-0 mb-1 xx-small"
              @click="realBuyForm.positionPct = getDefaultRealPositionPct(realBuyForm.strategy, realBuyForm.targetIdx)"
            >
              恢复默认
            </b-button>
          </div>
        </div>

        <!-- 风险提示 -->
        <div class="alert alert-warning p-2 xx-small mb-0 border-0 bg-soft-warning">
          <b-icon icon="exclamation-triangle-fill" class="mr-1"></b-icon>
          <strong>风险警示：</strong>点击确认后，系统将直接向券商外部通道提交实盘买入委托指令并推送微信通知。请核对标的及仓位无误后操作！
        </div>
      </div>

      <template #modal-footer="{ cancel }">
        <b-button variant="secondary" size="sm" class="px-3" :disabled="submittingRealBuy" @click="cancel()">
          取消
        </b-button>
        <b-button variant="danger" size="sm" class="px-4 font-weight-bold" :disabled="submittingRealBuy" @click="submitRealBuy">
          <b-spinner v-if="submittingRealBuy" small class="mr-1"></b-spinner>
          确认点火买入
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { request_zzquant } from 'network/request.js'
import StockInfoSidebar from '@/components/stock/StockInfoSidebar'
import TopicKlineModal from '@/components/plate/TopicKlineModal.vue'

export default {
  name: 'WonderStrategyPanorama',
  components: {
    StockInfoSidebar,
    TopicKlineModal
  },
  data() {
    const d = new Date()
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    const todayStr = `${year}-${month}-${day}`
    return {
      loading: true,
      topics: [],
      expandedTopics: {},
      sidebarSymbolCode: '',
      sidebarSymbolName: '',
      showTopicKlineModal: false,
      selectedTopicUniqueKey: '',
      selectedTopicName: '',
      selectedDate: todayStr,
      supplementH67: false,
      filterTopicNet5: true,
      v2PositionPcts: [25, 25],
      v2RealPositionPcts: [25, 25],
      v3PositionPcts: [15, 15, 8, 7, 5],
      v3RealPositionPcts: [15, 15, 8, 7, 5],
      crowding: { available: false, level: 'unknown' },
      todayBuys: { v2: [], v3: [] },
      showRealBuyModal: false,
      submittingRealBuy: false,
      realBuyForm: {
        code: '',
        name: '',
        price: null,
        strategy: 'v2',
        positionPct: 5.0,
        targetIdx: 0
      },
      savingConfig: false,
      refreshing: false,
      autoSync: false,
      lastSyncTime: '',
      silentSyncing: false,
      pendingSyncOnVisible: false,
      ws: null,
      wsReconnectTimer: null,
      isDarkMode: false,
      themeObserver: null
    }
  },
  computed: {
    crowdingBadgeVariant() {
      return { blocked: 'danger', reduced: 'warning', normal: 'success' }[this.crowding.level] || 'secondary'
    },
    crowdingLevelText() {
      return { blocked: '禁止开仓', reduced: '建议半仓', normal: '正常' }[this.crowding.level] || '暂无数据'
    },
    todayStr() {
      const d = new Date()
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    isCurrentDateToday() {
      return this.selectedDate === this.todayStr
    }
  },
  created() {
    const savedAutoSync = localStorage.getItem('panorama_auto_sync')
    if (savedAutoSync === 'true') {
      this.autoSync = true
    }
    this.initializePage()
  },
  mounted() {
    this.checkTheme()
    if (typeof MutationObserver !== 'undefined' && typeof document !== 'undefined') {
      this.themeObserver = new MutationObserver(() => {
        this.checkTheme()
      })
      this.themeObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] })
    }
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    if (this.autoSync && this.isCurrentDateToday) {
      this.initWebSocket()
    }
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    if (this.themeObserver) {
      this.themeObserver.disconnect()
      this.themeObserver = null
    }
    this.closeWebSocket()
  },
  methods: {
    checkTheme() {
      const siteTheme = typeof localStorage !== 'undefined' ? localStorage.getItem('site.theme') : null
      const bodyDark = typeof document !== 'undefined' && document.body.classList.contains('theme-dark')
      this.isDarkMode = siteTheme ? siteTheme === 'dark' : bodyDark
    },
    async refreshPage() {
      this.refreshing = true
      try {
        await this.initializePage()
      } finally {
        this.refreshing = false
      }
    },
    async initializePage() {
      try {
        const res = await request_zzquant({
          url: '/v3/topic/monitor/panorama/config',
          method: 'get',
          params: { date: this.selectedDate }
        })
        if (res.data && (res.data.code === 200 || res.data.code === 20000)) {
          const config = res.data.data || {}
          this.supplementH67 = Boolean(config.supplement_h67)
          this.filterTopicNet5 = config.filter_topic_net5 !== false
          this.v2PositionPcts = (config.v2_position_fractions || [0.25, 0.25]).map(value => Math.round(value * 1000) / 10)
          this.v3PositionPcts = (config.v3_position_fractions || [0.15, 0.15, 0.08, 0.07, 0.05]).map(value => Math.round(value * 1000) / 10)
          this.v2RealPositionPcts = (config.v2_real_position_fractions || config.v2_position_fractions || [0.25, 0.25]).map(value => Math.round(value * 1000) / 10)
          this.v3RealPositionPcts = (config.v3_real_position_fractions || config.v3_position_fractions || [0.15, 0.15, 0.08, 0.07, 0.05]).map(value => Math.round(value * 1000) / 10)
          this.crowding = config.crowding || { available: false, level: 'unknown' }
        }
      } catch (e) {
        console.error('读取全景配置失败，使用默认值', e)
      }
      await this.fetchTodayBuys()
      await this.fetchData()
      if (this.autoSync) {
        if (this.isCurrentDateToday) {
          this.initWebSocket()
        } else {
          this.closeWebSocket()
        }
      }
    },
    async fetchTodayBuys() {
      try {
        const requests = [
          request_zzquant({ url: '/v3/quant/strategy/daily-details', method: 'get', params: { date: this.selectedDate, account_id: 3 }, noRedirect: true }),
          request_zzquant({ url: '/v3/quant/strategy/daily-details', method: 'get', params: { date: this.selectedDate, account_id: 4 }, noRedirect: true })
        ]
        const [v2Res, v3Res] = await Promise.all(requests)
        this.todayBuys = {
          v2: this.extractBuyTargets(v2Res),
          v3: this.extractBuyTargets(v3Res)
        }
      } catch (e) {
        console.error('读取当日买入标的失败', e)
        this.todayBuys = { v2: [], v3: [] }
      }
    },
    extractBuyTargets(response) {
      const records = response?.data?.data?.records || []
      const seen = new Set()
      return records
        .filter(record => record.trade_type === '买入')
        .filter(record => {
          const key = record.symbol_code || record.symbol_name
          if (!key || seen.has(key)) return false
          seen.add(key)
          return true
        })
        .map(record => ({ code: record.symbol_code, name: record.symbol_name || record.symbol_code }))
    },
    todayBuyText(version) {
      const targets = this.todayBuys[version] || []
      return targets.length ? targets.map(item => item.name).join('、') : '暂无'
    },
    async updateMonitorConfig(refreshAfter = true) {
      if (this.selectedDate !== this.todayStr) {
        await this.fetchData()
        return
      }
      this.loading = true
      try {
        const res = await request_zzquant({
          url: '/v3/topic/monitor/panorama/config',
          method: 'post',
          data: {
            supplement_h67: this.supplementH67,
            filter_topic_net5: this.filterTopicNet5,
            v2_position_fractions: this.v2PositionPcts.map(value => Number(value) / 100),
            v3_position_fractions: this.v3PositionPcts.map(value => Number(value) / 100),
            v2_real_position_fractions: this.v2RealPositionPcts.map(value => Number(value) / 100),
            v3_real_position_fractions: this.v3RealPositionPcts.map(value => Number(value) / 100)
          }
        })
        if (!res.data || (res.data.code !== 200 && res.data.code !== 20000)) {
          throw new Error(res.data?.msg || res.data?.message || '保存配置失败')
        }
        this.crowding = res.data.data?.crowding || this.crowding
        this.$bvToast.toast('配置已保存，实盘将在下一监控周期刷新候选池', {
          title: '实盘配置',
          variant: 'success',
          solid: true
        })
      } catch (e) {
        console.error(e)
        this.$bvToast.toast(e.response?.data?.detail || e.message || '保存配置失败', {
          title: '错误',
          variant: 'danger',
          solid: true
        })
        await this.initializePage()
        return
      }
      if (refreshAfter) await this.fetchData()
    },
    positionTotal(values) {
      return values.reduce((total, value) => total + (Number(value) || 0), 0).toFixed(1)
    },
    recommendedPositionText(version) {
      const recommendation = this.crowding.recommendations?.[version]
      if (!recommendation) return '暂无'
      if (recommendation.total_position === 0) return '0%（禁止新开仓）'
      const details = recommendation.position_fractions.map(value => `${(value * 100).toFixed(1)}%`).join('/')
      const realDetails = recommendation.real_position_fractions ? recommendation.real_position_fractions.map(value => `${(value * 100).toFixed(1)}%`).join('/') : details
      return `模拟 ${(recommendation.total_position * 100).toFixed(1)}%（${details}） ｜ 实盘 ${(recommendation.real_total_position * 100).toFixed(1)}%（${realDetails}）`
    },
    async savePositionConfig() {
      const allValues = [
        ...this.v2PositionPcts,
        ...this.v3PositionPcts,
        ...this.v2RealPositionPcts,
        ...this.v3RealPositionPcts
      ]
      const invalid = allValues.some(value => !Number.isFinite(Number(value)) || Number(value) < 0 || Number(value) > 100)
      if (
        invalid ||
        Number(this.positionTotal(this.v2PositionPcts)) > 100 ||
        Number(this.positionTotal(this.v3PositionPcts)) > 100 ||
        Number(this.positionTotal(this.v2RealPositionPcts)) > 100 ||
        Number(this.positionTotal(this.v3RealPositionPcts)) > 100
      ) {
        this.$bvToast.toast('每笔仓位需在0%至100%之间（0为不开仓），单策略总仓位不能超过100%', { title: '仓位配置有误', variant: 'danger', solid: true })
        return
      }
      this.savingConfig = true
      try {
        await this.updateMonitorConfig(false)
      } finally {
        this.savingConfig = false
      }
    },
    toggleExpand(topicName) {
      this.$set(this.expandedTopics, topicName, !this.expandedTopics[topicName])
    },
    getDisplayCandidates(candidates, topicName) {
      if (!candidates) return []
      const isExpanded = this.expandedTopics[topicName]
      if (isExpanded || candidates.length <= 10) {
        return candidates
      }
      return candidates.slice(0, 10)
    },
    showStockInfo(code, name) {
      this.sidebarSymbolCode = code || ''
      this.sidebarSymbolName = name || ''
      this.$nextTick(() => {
        this.$root.$emit('bv::toggle::collapse', 'sidebar-right-stock')
      })
    },
    resetToToday() {
      this.selectedDate = this.todayStr
      this.initializePage()
    },
    async fetchData(silent = false) {
      if (!silent) {
        this.loading = true
      }
      try {
        const params = {
          supplement_h67: this.supplementH67,
          filter_topic_net5: this.filterTopicNet5
        }
        if (this.selectedDate) {
          params.date = this.selectedDate
        }
        const res = await request_zzquant({
          url: '/v3/topic/monitor/panorama',
          method: 'get',
          params
        })
        if (res.data && (res.data.code === 200 || res.data.code === 20000)) {
          this.topics = res.data.data
          if (silent) {
            const now = new Date()
            const h = String(now.getHours()).padStart(2, '0')
            const m = String(now.getMinutes()).padStart(2, '0')
            const s = String(now.getSeconds()).padStart(2, '0')
            this.lastSyncTime = `${h}:${m}:${s}`
          }
        } else if (!silent) {
          this.$bvToast.toast(res.data?.msg || res.data?.message || '获取数据失败', {
            title: '错误',
            variant: 'danger',
            solid: true
          })
        }
      } catch (e) {
        console.error(e)
        if (!silent) {
          this.$bvToast.toast('网络请求异常', {
            title: '错误',
            variant: 'danger',
            solid: true
          })
        }
      } finally {
        if (!silent) {
          this.loading = false
        }
      }
    },
    toggleAutoSync() {
      localStorage.setItem('panorama_auto_sync', this.autoSync ? 'true' : 'false')
      if (this.autoSync && this.isCurrentDateToday) {
        this.initWebSocket()
        this.performSilentSync()
      } else {
        this.closeWebSocket()
      }
    },
    handleVisibilityChange() {
      if (!document.hidden && this.pendingSyncOnVisible && this.autoSync && this.isCurrentDateToday) {
        this.pendingSyncOnVisible = false
        this.performSilentSync()
      }
    },
    async performSilentSync() {
      if (this.silentSyncing || !this.isCurrentDateToday) return
      this.silentSyncing = true
      try {
        await Promise.all([
          this.fetchData(true),
          this.fetchTodayBuys()
        ])
      } catch (e) {
        console.error('静默自动同步异常:', e)
      } finally {
        this.silentSyncing = false
      }
    },
    initWebSocket() {
      if (!this.autoSync || !this.isCurrentDateToday || this.ws) return

      const token = localStorage.getItem('token')
      if (!token) {
        this.$bvToast.toast('请先登录', { variant: 'danger' })
        this.autoSync = false
        return
      }

      const wsUrl = `wss://api.zizizaizai.com/v3/ws/topic_monitor?token=${encodeURIComponent(token)}`
      try {
        this.ws = new WebSocket(wsUrl)
        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data)
            if (data.type === 'panorama_tick' || data.type === 'topic_alert') {
              if (this.autoSync && this.isCurrentDateToday) {
                if (document.hidden) {
                  this.pendingSyncOnVisible = true
                } else {
                  this.performSilentSync()
                }
              }
            }
          } catch (e) {
            console.error('Error parsing WS message in Panorama:', e)
          }
        }
        this.ws.onclose = () => {
          this.ws = null
          if (this.autoSync && this.isCurrentDateToday) {
            this.wsReconnectTimer = setTimeout(() => this.initWebSocket(), 5000)
          }
        }
        this.ws.onerror = (err) => {
          console.error('Panorama WS error:', err)
        }
      } catch (err) {
        console.error('Failed to initialize Panorama WS:', err)
      }
    },
    closeWebSocket() {
      if (this.wsReconnectTimer) {
        clearTimeout(this.wsReconnectTimer)
        this.wsReconnectTimer = null
      }
      if (this.ws) {
        try {
          this.ws.onopen = null
          this.ws.onmessage = null
          this.ws.onclose = null
          this.ws.onerror = null
          this.ws.close()
        } catch (e) {
          // ignore
        }
        this.ws = null
      }
    },
    getPnlClass(val) {
      if (val > 0) return 'text-danger'
      if (val < 0) return 'text-success'
      return 'text-muted'
    },
    formatTopicNet5(topic) {
      if (!topic) return ''
      const net = topic.net5 !== undefined && topic.net5 !== null ? topic.net5 : topic.NET5
      if (net === undefined || net === null) return ''
      const sign = net > 0 ? '+' : ''
      if (topic.limit_up5 !== undefined && topic.limit_up5 !== null && topic.limit_down5 !== undefined && topic.limit_down5 !== null) {
        return `${sign}${net}(${topic.limit_up5}/${topic.limit_down5})`
      }
      return `${sign}${net}`
    },
    getNet5BadgeClass(topic) {
      if (!topic) return ''
      const net = topic.net5 !== undefined && topic.net5 !== null ? topic.net5 : topic.NET5
      if (net === undefined || net === null) return ''
      if (net > 0) return 'badge-net-positive'
      if (net < 0) return 'badge-net-negative'
      return 'badge-net-zero'
    },
    showTopicKline(topic) {
      if (topic && topic.unique_key) {
        this.selectedTopicUniqueKey = topic.unique_key
        this.selectedTopicName = topic.topic_name
        this.showTopicKlineModal = true
      }
    },
    getDefaultRealPositionPct(strategy, targetIdx = 0) {
      const pcts = strategy === 'v3' ? this.v3RealPositionPcts : this.v2RealPositionPcts
      if (Array.isArray(pcts) && pcts.length > 0) {
        if (targetIdx >= 0 && targetIdx < pcts.length && Number(pcts[targetIdx]) > 0) {
          return Number(pcts[targetIdx])
        }
        const firstNonZero = pcts.find(p => Number(p) > 0)
        if (firstNonZero !== undefined) {
          return Number(firstNonZero)
        }
        return Number(pcts[0]) || 5.0
      }
      return 5.0
    },
    openRealBuyModal(item, version = 'v2', idx = 0) {
      const defaultStrategy = version === 'v3' ? 'v3' : 'v2'
      const defaultPct = this.getDefaultRealPositionPct(defaultStrategy, idx)
      this.realBuyForm = {
        code: item.code,
        name: item.name || item.code,
        price: item.price || null,
        strategy: defaultStrategy,
        positionPct: defaultPct,
        targetIdx: idx
      }
      this.showRealBuyModal = true
    },
    onRealBuyStrategyChange(strategy) {
      this.realBuyForm.strategy = strategy
      this.realBuyForm.positionPct = this.getDefaultRealPositionPct(strategy, this.realBuyForm.targetIdx)
    },
    async submitRealBuy() {
      const pct = Number(this.realBuyForm.positionPct)
      if (!pct || pct <= 0 || pct > 100) {
        this.$bvToast.toast('请输入有效的买入仓位比例（0.1% ~ 100%）', {
          title: '提示',
          variant: 'warning',
          solid: true
        })
        return
      }
      if (!this.realBuyForm.code) {
        this.$bvToast.toast('标的代码缺失', {
          title: '错误',
          variant: 'danger',
          solid: true
        })
        return
      }
      this.submittingRealBuy = true
      try {
        const res = await request_zzquant({
          url: '/v3/topic/monitor/trigger_real_buy',
          method: 'post',
          data: {
            code: this.realBuyForm.code,
            name: this.realBuyForm.name,
            strategy: this.realBuyForm.strategy,
            position_percent: pct / 100
          }
        })
        if (res.data && (res.data.code === 200 || res.data.code === 20000)) {
          this.$bvToast.toast(res.data.message || `标的 ${this.realBuyForm.name} 实盘点火买入成功！`, {
            title: '实盘买入成功',
            variant: 'success',
            solid: true
          })
          this.showRealBuyModal = false
          this.fetchTodayBuys()
        } else {
          this.$bvToast.toast(res.data?.msg || res.data?.message || '实盘买入点火失败', {
            title: '点火失败',
            variant: 'danger',
            solid: true
          })
        }
      } catch (e) {
        console.error('实盘点火买入异常', e)
        const errMsg = e.response?.data?.detail || e.message || '网络请求异常'
        this.$bvToast.toast(errMsg, {
          title: '点火异常',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.submittingRealBuy = false
      }
    },
    confirmManualTrigger(code6, name) {
      this.openRealBuyModal({ code: code6, name: name }, 'v2', 0)
    },
    async triggerManual(code6) {
      try {
        const res = await request_zzquant({
          url: '/v3/topic/monitor/trigger_manual',
          method: 'post',
          data: {
            code: code6
          }
        })
        if (res.data && res.data.code === 200) {
          this.$bvToast.toast(`个股 ${code6} 手动点火成功！`, {
            title: '成功',
            variant: 'success',
            solid: true
          })
          this.fetchData()
        } else {
          this.$bvToast.toast(res.data?.msg || res.data?.message || '点火失败', {
            title: '错误',
            variant: 'danger',
            solid: true
          })
        }
      } catch (e) {
        console.error(e)
        const errMsg = e.response?.data?.detail || '网络请求异常'
        this.$bvToast.toast(errMsg, {
          title: '错误',
          variant: 'danger',
          solid: true
        })
      }
    },
    shortenStatus(text) {
      if (!text || text === '-') return '-'
      let t = String(text).trim()
      t = t.replace('below_MA20', '破MA20')
      t = t.replace('no_new_high', '非新高')
      t = t.replace('非近30日新高', '非新高')
      t = t.replace('上市不足1年', '次新')
      t = t.replace('次新股', '次新')
      t = t.replace('昨日涨幅过大', '昨大涨')
      t = t.replace('未触发(已达)', '已达标')

      t = t.replace('避(偏离超买)', '避超买')
      t = t.replace('避(市值<54亿)', '避市值')
      t = t.replace('避(题材排除)', '避题材')
      t = t.replace('避(已持仓)', '避持仓')
      t = t.replace('避(今日卖出)', '避今卖')
      t = t.replace('避(低开<-8%)', '避低开')
      t = t.replace('避(昨日涨幅过大)', '避昨涨')
      t = t.replace('避雷针', '避雷')

      if (t.startsWith('买入(')) {
        t = '买' + t.slice(3, -1)
      } else if (t === '买入') {
        t = '买'
      } else if (t.startsWith('静默(')) {
        t = '静' + t.slice(3, -1)
      } else if (t === '静默') {
        t = '静'
      } else if (t.startsWith('触发(')) {
        t = '触' + t.slice(3, -1)
      } else if (t === '触发') {
        t = '触'
      } else if (t.startsWith('占位(')) {
        t = '占' + t.slice(3, -1)
      } else if (t === '占位') {
        t = '占'
      }
      return t
    },
    getRawV2Text(stock) {
      if (stock && stock.v2 && stock.v2.text) return stock.v2.text
      if (stock && stock.triggered) return `触发${stock.trigger_time ? `(${stock.trigger_time})` : ''}`
      if (stock && stock.filter_reason === '占位') return `占位${stock.trigger_time ? `(${stock.trigger_time})` : ''}`
      if (stock && stock.filter_reason) return stock.filter_reason
      if (stock && stock.is_met_buy_condition) return '未触发(已达)'
      return '未触发'
    },
    getV2BadgeText(stock) {
      return this.shortenStatus(this.getRawV2Text(stock))
    },
    getRawV3Text(stock) {
      if (stock && stock.v3 && stock.v3.text) return stock.v3.text
      return '-'
    },
    getV3BadgeText(stock) {
      return this.shortenStatus(this.getRawV3Text(stock))
    },
    isStatusMerged(stock) {
      const v2 = this.getV2BadgeText(stock)
      const v3 = this.getV3BadgeText(stock)
      if (v3 === '-') return true
      return v2 === v3
    },
    getMergedBadgeText(stock) {
      return this.getV2BadgeText(stock)
    },
    getMergedBadgeClass(stock) {
      return this.getV2BadgeClass(stock)
    },
    getStatusTooltip(stock) {
      const v2 = this.getRawV2Text(stock)
      const v3 = this.getRawV3Text(stock)
      return `V2: ${v2} | V3: ${v3}`
    },
    formatAmountMkv(amount, mkv) {
      const a = (amount || 0) / 100000000
      const m = (mkv || 0) / 100000000
      const aStr = a >= 10 ? a.toFixed(0) : a.toFixed(1)
      const mStr = m >= 10 ? m.toFixed(0) : m.toFixed(1)
      return `${aStr}/${mStr}`
    },
    getV2BadgeClass(stock) {
      const type = stock?.v2?.badge_type || stock?.v2?.status
      if (type === 'buy' || stock?.triggered) return 'badge-buy'
      if (type === 'placeholder' || stock?.filter_reason === '占位') return 'badge-placeholder'
      if (type === 'avoid' || (stock?.filter_reason && (stock.filter_reason.indexOf('避') === 0 || stock.filter_reason.indexOf('避雷') > -1))) return 'badge-avoid'
      if (type === 'muted' || (stock?.filter_reason && !stock.triggered)) return 'badge-muted-tag'
      if (type === 'met' || stock?.is_met_buy_condition) return 'badge-met-condition'
      return 'badge-untriggered'
    },
    getV3BadgeClass(stock) {
      const type = stock?.v3?.badge_type || stock?.v3?.status
      if (type === 'buy' || type === 'triggered') return 'badge-buy'
      if (type === 'silent') return 'badge-v3-silent'
      if (type === 'avoid') return 'badge-avoid'
      if (type === 'muted') return 'badge-muted-tag'
      if (type === 'met') return 'badge-met-condition'
      return 'badge-untriggered'
    },
    isCanManualTrigger(stock) {
      if (!stock) return false
      if (stock.triggered) return false
      if (stock.v2 && (stock.v2.bought || stock.v2.status === 'triggered' || stock.v2.status === 'buy')) {
        return false
      }
      return true
    },
    getScoreClass(score) {
      if (score >= 3) return 'score-high'
      if (score >= 1) return 'score-mid'
      return 'score-low'
    },
    getScoreTooltip(stock) {
      if (!stock || stock.score === undefined || stock.score === null) return ''
      const details = stock.score_details || {}

      let timing = details.timing !== undefined ? details.timing : null
      let ma5 = details.ma5 !== undefined ? details.ma5 : (details.dist_ma5 !== undefined ? details.dist_ma5 : null)
      let window = details.window !== undefined ? details.window : (details.time !== undefined ? details.time : null)
      let upRatio = details.up_ratio !== undefined ? details.up_ratio : (details.breadth !== undefined ? details.breadth : null)
      let avgPct = details.avg_pct !== undefined ? details.avg_pct : (details.theme_pct !== undefined ? details.theme_pct : null)

      // 若 details 字段缺失，从 score_str (格式如 '+2 (0+0+1+1+0)') 兜底解析 [up_ratio, avg_pct, timing, window, ma5]
      if (timing === null || ma5 === null || window === null || upRatio === null || avgPct === null) {
        if (stock.score_str && stock.score_str.indexOf('(') > -1) {
          const m = stock.score_str.match(/\(([^)]+)\)/)
          if (m && m[1]) {
            const nums = m[1].match(/[+-]?\d+/g)
            if (nums && nums.length >= 5) {
              if (upRatio === null) upRatio = parseInt(nums[0], 10) || 0
              if (avgPct === null) avgPct = parseInt(nums[1], 10) || 0
              if (timing === null) timing = parseInt(nums[2], 10) || 0
              if (window === null) window = parseInt(nums[3], 10) || 0
              if (ma5 === null) ma5 = parseInt(nums[4], 10) || 0
            }
          }
        }
      }

      timing = timing ?? 0
      ma5 = ma5 ?? 0
      window = window ?? 0
      upRatio = upRatio ?? 0
      avgPct = avgPct ?? 0

      const fmt = (label, val) => `${label}:${val > 0 ? '+' : ''}${val}`
      const dStr = [
        fmt('大盘', timing),
        fmt('均线', ma5),
        fmt('时效', window),
        fmt('广度', upRatio),
        fmt('题材', avgPct)
      ].join(' | ')

      const raw = details.raw || {}
      const parts = []
      if (raw.dist_ma5 !== undefined && raw.dist_ma5 !== null) parts.push(`乖离MA5:${raw.dist_ma5}%`)
      if (raw.trigger_time) parts.push(`时点:${raw.trigger_time}`)
      if (raw.up_count !== undefined && raw.down_count !== undefined) parts.push(`板块涨跌:${raw.up_count}/${raw.down_count}`)
      if (raw.avg_pct !== undefined && raw.avg_pct !== null) parts.push(`板块均幅:${raw.avg_pct}%`)

      const rawSuffix = parts.length > 0 ? `\n(${parts.join(', ')})` : ''
      return `确定性评分: ${stock.score > 0 ? '+' : ''}${stock.score} (${dStr})${rawSuffix}`
    }
  }
}
</script>

<style scoped>
.panorama-page {
  min-height: 100vh;
  background-color: var(--bg-main, #f8fafc);
  color: var(--text-main, #1e293b);
  --p-card-bg: var(--bg-card, #ffffff);
  --p-border: var(--border-color, #e2e8f0);
  --p-text-main: var(--text-main, #1e293b);
  --p-text-muted: var(--text-muted, #64748b);
  --p-pill-bg: rgba(0, 0, 0, 0.04);
  --p-pill-border: rgba(0, 0, 0, 0.1);
  --p-input-bg: #ffffff;
  --p-input-border: #cbd5e1;
  --p-input-color: #1e293b;
  --p-table-hover: rgba(0, 0, 0, 0.03);
  transition: background-color 0.2s ease, color 0.2s ease;
}

.panorama-page.dark-mode {
  background-color: #0f1219;
  color: #ffffff;
  --p-card-bg: #1a1e29;
  --p-border: rgba(255, 255, 255, 0.1);
  --p-text-main: #ffffff;
  --p-text-muted: #94a3b8;
  --p-pill-bg: rgba(255, 255, 255, 0.08);
  --p-pill-border: rgba(255, 255, 255, 0.15);
  --p-input-bg: rgba(255, 255, 255, 0.06);
  --p-input-border: rgba(255, 255, 255, 0.14);
  --p-input-color: #ffffff;
  --p-table-hover: rgba(255, 255, 255, 0.05);
}

.text-title,
.topic-name-title,
.stock-name-link,
.leader-names,
.target-name,
.counts-text {
  color: var(--p-text-main) !important;
}

.panorama-card {
  background-color: var(--p-card-bg) !important;
  border: 1px solid var(--p-border) !important;
  color: var(--p-text-main);
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.panorama-divider {
  border-color: var(--p-border) !important;
}

.panorama-pill {
  background-color: var(--p-pill-bg);
  border-color: var(--p-pill-border) !important;
}

.custom-datepicker {
  color: var(--p-text-main) !important;
}
.custom-datepicker::-webkit-calendar-picker-indicator {
  cursor: pointer;
}
.dark-mode .custom-datepicker::-webkit-calendar-picker-indicator {
  filter: invert(0.8);
}

.panorama-switch {
  color: var(--p-text-main);
}

.target-summary-card {
  background-color: var(--p-pill-bg);
  border: 1px solid var(--p-border);
}

.position-config-row {
  display: flex;
  align-items: flex-end;
  gap: 0.4rem;
  flex-wrap: wrap;
}
.strategy-label {
  min-width: 52px;
  padding-bottom: 0.3rem;
  font-weight: 700;
  font-size: 0.8rem;
}
.position-input-wrap {
  width: 78px;
}
.position-input-wrap label {
  color: var(--p-text-muted);
  display: block;
  font-size: 0.65rem;
  margin-bottom: 0;
  line-height: 1.1;
}
.position-input-wrap .form-control,
.position-input-wrap .input-group-text {
  background: var(--p-input-bg);
  border-color: var(--p-input-border);
  color: var(--p-input-color);
  height: 27px;
  padding-top: 0.15rem;
  padding-bottom: 0.15rem;
  font-size: 0.75rem;
}
.position-total {
  color: var(--p-text-muted);
  font-size: 0.75rem;
  padding-bottom: 0.3rem;
  margin-left: auto;
}
.position-total strong { color: var(--p-text-main); }
.today-buy-targets strong { color: #0284c7; font-weight: 600; }
.dark-mode .today-buy-targets strong { color: #7dd3fc; }

.crowding-card {
  background: var(--p-card-bg);
}
.crowding-normal { border-left: 4px solid #28a745 !important; }
.crowding-reduced { border-left: 4px solid #ffc107 !important; }
.crowding-blocked { border-left: 4px solid #dc3545 !important; }
.crowding-unknown { border-left: 4px solid #6c757d !important; }
.crowding-ratio { color: var(--p-text-main); font-size: 1.75rem; font-weight: 700; line-height: 1; }
.crowding-threshold-note { color: var(--p-text-muted); font-size: 0.65rem; white-space: nowrap; }
.crowding-advice { color: #b45309; font-size: 0.85rem; font-weight: 600; }
.dark-mode .crowding-advice { color: #f3d58a; }
.crowding-result { min-width: 0; }
.topic-sequence { color: var(--p-text-muted); font-variant-numeric: tabular-nums; }
@media (max-width: 767.98px) {
  .position-input-wrap { width: calc(50% - 34px); }
  .position-total { width: 100%; margin-left: 34px; }
  .crowding-content { align-items: flex-start !important; flex-wrap: wrap; }
  .crowding-result { margin-top: 0.4rem; width: 100%; }
}
.text-muted-gold {
  color: #bfa77b !important;
}
.hover-primary:hover {
  color: var(--primary) !important;
}
.x-small {
  font-size: 0.75rem;
}
.xx-small {
  font-size: 0.65rem;
}
.compact-table td, .compact-table th {
  padding: 0.16rem 0.12rem !important;
  vertical-align: middle !important;
}
.custom-row-5 > .col-xl-20 {
  flex: 0 0 20%;
  max-width: 20%;
}

.panorama-table {
  background-color: transparent;
  color: var(--p-text-main);
}
.panorama-table th {
  color: var(--p-text-muted);
  border-bottom: 1px solid var(--p-border) !important;
}
.panorama-table td {
  border-top: 1px solid var(--p-border);
}
.panorama-table tbody tr:hover {
  background-color: var(--p-table-hover);
}

/* 徽章配色：默认高对比度浅色模式 */
.badge-met-condition {
  background-color: #f3e8ff !important;
  color: #6d28d9 !important;
  border: 1px solid #d8b4fe !important;
}
.badge-avoid {
  background-color: #dbeafe !important;
  color: #1d4ed8 !important;
  border: 1px solid #93c5fd !important;
}
.badge-net-positive {
  background-color: #fee2e2 !important;
  color: #b91c1c !important;
  border: 1px solid #fca5a5 !important;
}
.badge-net-zero {
  background-color: #f1f5f9 !important;
  color: #475569 !important;
  border: 1px solid #cbd5e1 !important;
}
.badge-net-negative {
  background-color: #dcfce7 !important;
  color: #15803d !important;
  border: 1px solid #86efac !important;
}
.badge-collapse {
  background-color: #fee2e2 !important;
  color: #b91c1c !important;
  border: 1px solid #f87171 !important;
}
.badge-buy {
  background-color: #fee2e2 !important;
  color: #b91c1c !important;
  border: 1px solid #f87171 !important;
}
.badge-v3-silent {
  background-color: #fef3c7 !important;
  color: #b45309 !important;
  border: 1px solid #fcd34d !important;
}
.badge-placeholder {
  background-color: #fef9c3 !important;
  color: #a16207 !important;
  border: 1px solid #fde047 !important;
}
.badge-muted-tag {
  background-color: #f1f5f9 !important;
  color: #64748b !important;
  border: 1px solid #e2e8f0 !important;
}
.badge-untriggered {
  background-color: #f8fafc !important;
  color: #94a3b8 !important;
  border: 1px solid #e2e8f0 !important;
}

/* 深色模式下的徽章霓虹暗底配色 */
.dark-mode .badge-met-condition {
  background-color: rgba(139, 92, 246, 0.18) !important;
  color: #c084fc !important;
  border: 1px solid rgba(139, 92, 246, 0.4) !important;
}
.dark-mode .badge-avoid {
  background-color: rgba(59, 130, 246, 0.16) !important;
  color: #93c5fd !important;
  border: 1px solid rgba(59, 130, 246, 0.35) !important;
}
.dark-mode .badge-net-positive {
  background-color: rgba(239, 68, 68, 0.2) !important;
  color: #f87171 !important;
  border: 1px solid rgba(239, 68, 68, 0.4) !important;
}
.dark-mode .badge-net-zero {
  background-color: rgba(148, 163, 184, 0.15) !important;
  color: #94a3b8 !important;
  border: 1px solid rgba(148, 163, 184, 0.3) !important;
}
.dark-mode .badge-net-negative {
  background-color: rgba(34, 197, 94, 0.2) !important;
  color: #4ade80 !important;
  border: 1px solid rgba(34, 197, 94, 0.4) !important;
}
.dark-mode .badge-collapse {
  background-color: rgba(239, 68, 68, 0.22) !important;
  color: #fca5a5 !important;
  border: 1px solid rgba(239, 68, 68, 0.5) !important;
}
.dark-mode .badge-buy {
  background-color: rgba(220, 53, 69, 0.24) !important;
  color: #ff6b6b !important;
  border: 1px solid rgba(220, 53, 69, 0.55) !important;
}
.dark-mode .badge-v3-silent {
  background-color: rgba(245, 158, 11, 0.2) !important;
  color: #fcd34d !important;
  border: 1px solid rgba(245, 158, 11, 0.45) !important;
}
.dark-mode .badge-placeholder {
  background-color: rgba(234, 179, 8, 0.2) !important;
  color: #fde047 !important;
  border: 1px solid rgba(234, 179, 8, 0.45) !important;
}
.dark-mode .badge-muted-tag {
  background-color: rgba(148, 163, 184, 0.1) !important;
  color: #94a3b8 !important;
  border: 1px solid rgba(148, 163, 184, 0.22) !important;
}
.dark-mode .badge-untriggered {
  background-color: rgba(255, 255, 255, 0.04) !important;
  color: #64748b !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.micro-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.6rem !important;
  line-height: 1.15;
  padding: 1px 3px !important;
  border-radius: 2px;
  letter-spacing: -0.2px;
  white-space: nowrap;
}
.micro-prefix {
  font-weight: 700;
  margin-right: 1px;
  font-size: 0.58rem;
}
.v2-p {
  color: #d97706;
}
.dark-mode .v2-p {
  color: #ffc107;
}
.v3-p {
  color: #0284c7;
}
.dark-mode .v3-p {
  color: #0dcaf0;
}
.status-badge-compact {
  font-size: 0.61rem !important;
  padding: 1px 4px !important;
  line-height: 1.15;
  border-radius: 2px;
}

.fire-btn {
  font-size: 0.68rem !important;
  line-height: 1 !important;
  cursor: pointer;
  filter: drop-shadow(0 0 2px rgba(255, 193, 7, 0.6));
  transition: transform 0.15s ease;
  vertical-align: middle;
  text-decoration: none !important;
}
.fire-btn:hover {
  transform: scale(1.25);
}

.score-pill {
  display: inline-block;
  font-family: var(--font-monospace, monospace);
  font-size: 0.58rem;
  font-weight: 700;
  line-height: 1;
  padding: 1px 3px;
  border-radius: 2px;
  cursor: help;
  white-space: nowrap;
}
.score-high {
  color: #047857;
  background-color: #d1fae5;
  border: 1px solid #6ee7b7;
}
.score-mid {
  color: #0284c7;
  background-color: #e0f2fe;
  border: 1px solid #7dd3fc;
}
.score-low {
  color: #475569;
  background-color: #f1f5f9;
  border: 1px solid #cbd5e1;
}

.dark-mode .score-high {
  color: #10b981;
  background-color: rgba(16, 185, 129, 0.16);
  border: 1px solid rgba(16, 185, 129, 0.38);
}
.dark-mode .score-mid {
  color: #38bdf8;
  background-color: rgba(56, 189, 248, 0.14);
  border: 1px solid rgba(56, 189, 248, 0.32);
}
.dark-mode .score-low {
  color: #94a3b8;
  background-color: rgba(148, 163, 184, 0.1);
  border: 1px solid rgba(148, 163, 184, 0.22);
}

.border-success-50 {
  border-color: rgba(40, 167, 69, 0.55) !important;
  box-shadow: 0 0 6px rgba(40, 167, 69, 0.2);
}
.pulse-dot {
  display: inline-block;
  color: #28a745;
  animation: pulse-dot-anim 2s infinite ease-in-out;
}
@keyframes pulse-dot-anim {
  0% { transform: scale(0.9); opacity: 0.5; }
  50% { transform: scale(1.25); opacity: 1; text-shadow: 0 0 4px #28a745; }
  100% { transform: scale(0.9); opacity: 0.5; }
}
.today-buy-target-item {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}
.fire-buy-btn {
  font-size: 0.85rem;
  line-height: 1;
  vertical-align: middle;
  text-decoration: none !important;
  margin-left: 2px;
  cursor: pointer;
  transition: transform 0.15s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
.fire-buy-btn:hover {
  transform: scale(1.35);
  filter: drop-shadow(0 0 5px rgba(255, 68, 0, 0.9));
}
.bg-soft-warning {
  background-color: rgba(255, 193, 7, 0.12) !important;
  color: #ffc107 !important;
}
.strategy-select-group .btn {
  border-radius: 6px;
  transition: all 0.2s ease;
}
</style>

<style>
/* 全局深色模式穿透适配 */
body.theme-dark .panorama-page {
  background-color: #0f1219;
  color: #ffffff;
  --p-card-bg: #1a1e29;
  --p-border: rgba(255, 255, 255, 0.1);
  --p-text-main: #ffffff;
  --p-text-muted: #94a3b8;
  --p-pill-bg: rgba(255, 255, 255, 0.08);
  --p-pill-border: rgba(255, 255, 255, 0.15);
  --p-input-bg: rgba(255, 255, 255, 0.06);
  --p-input-border: rgba(255, 255, 255, 0.14);
  --p-input-color: #ffffff;
  --p-table-hover: rgba(255, 255, 255, 0.05);
}
body.theme-dark .custom-datepicker::-webkit-calendar-picker-indicator {
  filter: invert(0.8);
}
body.theme-dark .badge-collapse {
  background-color: rgba(239, 68, 68, 0.22) !important;
  color: #fca5a5 !important;
  border: 1px solid rgba(239, 68, 68, 0.5) !important;
}
body.theme-dark .badge-net-positive {
  background-color: rgba(239, 68, 68, 0.2) !important;
  color: #f87171 !important;
  border: 1px solid rgba(239, 68, 68, 0.4) !important;
}
body.theme-dark .badge-net-zero {
  background-color: rgba(148, 163, 184, 0.15) !important;
  color: #94a3b8 !important;
  border: 1px solid rgba(148, 163, 184, 0.3) !important;
}
body.theme-dark .badge-net-negative {
  background-color: rgba(34, 197, 94, 0.2) !important;
  color: #4ade80 !important;
  border: 1px solid rgba(34, 197, 94, 0.4) !important;
}
body.theme-dark .badge-avoid {
  background-color: rgba(59, 130, 246, 0.16) !important;
  color: #93c5fd !important;
  border: 1px solid rgba(59, 130, 246, 0.35) !important;
}
body.theme-dark .badge-met-condition {
  background-color: rgba(139, 92, 246, 0.18) !important;
  color: #c084fc !important;
  border: 1px solid rgba(139, 92, 246, 0.4) !important;
}
body.theme-dark .badge-buy {
  background-color: rgba(220, 53, 69, 0.24) !important;
  color: #ff6b6b !important;
  border: 1px solid rgba(220, 53, 69, 0.55) !important;
}
body.theme-dark .badge-v3-silent {
  background-color: rgba(245, 158, 11, 0.2) !important;
  color: #fcd34d !important;
  border: 1px solid rgba(245, 158, 11, 0.45) !important;
}
body.theme-dark .badge-placeholder {
  background-color: rgba(234, 179, 8, 0.2) !important;
  color: #fde047 !important;
  border: 1px solid rgba(234, 179, 8, 0.45) !important;
}
body.theme-dark .badge-muted-tag {
  background-color: rgba(148, 163, 184, 0.1) !important;
  color: #94a3b8 !important;
  border: 1px solid rgba(148, 163, 184, 0.22) !important;
}
body.theme-dark .badge-untriggered {
  background-color: rgba(255, 255, 255, 0.04) !important;
  color: #64748b !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}
body.theme-dark .score-high {
  color: #10b981;
  background-color: rgba(16, 185, 129, 0.16);
  border: 1px solid rgba(16, 185, 129, 0.38);
}
body.theme-dark .score-mid {
  color: #38bdf8;
  background-color: rgba(56, 189, 248, 0.14);
  border: 1px solid rgba(56, 189, 248, 0.32);
}
body.theme-dark .score-low {
  color: #94a3b8;
  background-color: rgba(148, 163, 184, 0.1);
  border: 1px solid rgba(148, 163, 184, 0.22);
}
body.theme-dark .today-buy-targets strong {
  color: #7dd3fc;
}
body.theme-dark .crowding-advice {
  color: #f3d58a;
}
body.theme-dark .v2-p {
  color: #ffc107;
}
body.theme-dark .v3-p {
  color: #0dcaf0;
}
</style>
